import React, { useState, useEffect, useContext } from "react";
import { Form, Button } from "react-bootstrap";
import axios from "axios";
import useCookie from "../../hooks/useCookie";
import { EntityManagementContext } from "../../context/EntityManagementContext";

// Create new or edit existing class entity
function ManageClassForm(props) {
  const {
    editingID,
    mode,
    setMode,
    teachers,
    exit,
    updateEntities
  } = useContext(EntityManagementContext);

  // Value of form elements; used to load data in edit mode
  // and grabbed upon save
  const [className, setClassName] = useState("");
  const [classGrade, setClassGrade] = useState("");
  const [teacher, setTeacher] = useState("");

  const [success, setSuccess] = useState(false);

  // When set to true, displays an error badge that helps
  // user correct their error.
  const [errorState, setErrorState] = useState(false);

  // List of errors to display after creation/editing error
  // occurs; implemented as dictionary mapping input field
  // to associated list of error messages.
  const [errorDict, setErrorDict] = useState({});

  const csrftoken = useCookie("csrftoken");

  // Badge that appears upon sucessful creation/edit of class
  const successBadge = () => {
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
    }, 5000);
  };

  // If a class has been selected to edit, populate
  // all form options with currently saved data
  useEffect(() => {
    if (mode == "Edit") {
      // Clear any error messages from previous
      // operations.
      setErrorState(false);
      setErrorDict({});

      axios.get("/api/classes/" + editingID)
        .then((response) => {
          let editingClass = response.data;
          setClassName(editingClass.className);
          setClassGrade(editingClass.classGrade);
          setTeacher(editingClass.teacher);
        });
    } else if (mode == "Create") {
      // Clear any error messages from previous
      // operations.
      setErrorState(false);
      setErrorDict({});

      // Clear form
      clearForm();
    }
  }, [editingID]);

  // Clear all form values
  const clearForm = () => {
    setClassName("");
    setClassGrade("");
    setTeacher("");
  }

  // Cancel creation or editing of class
  const cancelAction = () => {
    if (mode == "Edit") {
      setMode("Create");
    }
    clearForm();
    exit();
  }

  // Organize validation error information for error badge display.
  //  errorObj: HTTP response carrying error information, as JSON object
  const handleFieldValidationErrors = (errorObj) => {
    // Gather errors, which will we use to update state at the end
    let currentErrorDict = {};

    // Get response body
    let errorBody = errorObj.response.data;

    // This is MVP - just handling errors from server-side validation
    // Would be better if we had client-side validation as well to make 
    // the user registration process faster

    // Handle first name related errors
    if (errorBody["className"] && errorBody["className"].length >= 1) {
      currentErrorDict["Class name"] = errorBody["className"];
    }

    setErrorDict(currentErrorDict);
    setErrorState(true);
  }

  // Submission of form
  // Depending on current mode, creates or saves entity to database
  const handleSubmit = async (e) => {
    // Prevent default behavior - do not clear inputs immediately
    e.preventDefault();

    const headers = {
      "X-CSRFToken": csrftoken,
    };

    // Use form values to create new entity in database
    if (mode == "Create") {
      const newClass = {
        className: className,
        classGrade: classGrade,
        teacher: teacher,
        prompts: null,
      };
      axios
        .post("/api/classes/", newClass, { headers })
        .then((response) => {
          successBadge();
          clearForm();
          // Update table with new class
          updateEntities(response.data);
        })
        .catch(error => {
          handleFieldValidationErrors(error);
        });
    } else if (mode == "Edit") { // Update existing entity in database
      const updatedClass = {
        className: className,
        classGrade: classGrade,
        teacher: teacher
      };
      axios.put("/api/classes/" + editingID, updatedClass, { headers })
        .then((response) => {
          if (response.status == 200) {
            successBadge();
            // Clear form values
            clearForm();
            // Set mode back to create
            setMode("Create");
            // Update table with updated class
            updateEntities(response.data);
          }
        })
        .catch(error => {
          handleFieldValidationErrors(error);
        });
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group controlId="formBasicClassName">
        <Form.Label>Class Name</Form.Label> {/* Form item: Class name */}
        <Form.Control
          type="text"
          placeholder="Enter class name"
          value={className}
          onChange={(e) => setClassName(e.target.value)}
          required
        />
      </Form.Group>

      <Form.Group controlId="formBasicClassGrade"> {/* Form item: Class grade (dropdown) */}
        <Form.Label>Class Grade</Form.Label>
        <select name="classGrade" id="grade-dropdown" value={classGrade} onChange={(e) => setClassGrade(e.target.value)} required>
          <option value="" disabled selected hidden>Choose a grade...</option>
          <option value="K">Kindergarten</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="4">4</option>
          <option value="5">5</option>
          <option value="6">6</option>
          <option value="7">7</option>
          <option value="8">8</option>
          <option value="9">9</option>
          <option value="10">10</option>
          <option value="11">11</option>
          <option value="12">12</option>
        </select>
      </Form.Group>

      <Form.Group controlId="formBasicTeacher"> {/* Form item: Teacher */}
        <Form.Label>Teacher</Form.Label>
        <select name="teacher" id="grade-dropdown" value={teacher} onChange={(e) => setTeacher(e.target.value)} required>
          <option value="" disabled selected hidden>Choose a teacher...</option>
          {teachers.map((teacher) => <option value={teacher.id}>{teacher.first_name} {teacher.last_name} ({teacher.email})</option>)}
        </select>
      </Form.Group>

      {/* Submit button */}
      <Button
        id="addClass"
        className="admin-button"
        type="submit"
        style={{ marginTop: "5px" }}
      >
        {mode} Class
      </Button>

      {/* Cancel button */}
      <Button
        className="admin-button"
        onClick={cancelAction}
        style={{ marginTop: "5px" }}
      >
        Cancel
      </Button>

      {/* Show success badge if action was successful */}
      {success ? (
        <div
          className="alert alert-success"
          role="alert"
          style={{ marginTop: "5px" }}
        >
          Successful!
        </div>
      ) : null}

      {/* If there are any erroneous inputs, let the user know */}
      {errorState ? (
        <div
          className="alert alert-danger"
          role="alert"
          style={{ marginTop: "5px" }}
        >
          <strong>An error occurred.</strong><br /><br />
          {Object.entries(errorDict).map(
            (field) =>
              <>
                <i>{field[0]}</i>
                <ul>
                  {field[1].map((error) =>
                    <li>{error}</li>
                  )}
                </ul>
              </>
          )}
        </div>
      ) : null}
    </Form>
  );
}

export default ManageClassForm;
